import React from "react";
import { Box, Card, Stack, Typography } from "@mui/material";

const formatMoney = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR" }).format(
    Number(v) || 0
  );

const DataRow = ({ label, value, copyable }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "220px 1fr" },
      gap: { xs: 0.25, sm: 2 },
      py: 1.25,
      borderBottom: (t) => `1px dashed ${t.palette.divider}`,
      "&:last-of-type": { borderBottom: 0 },
    }}
  >
    <Typography
      variant="caption"
      sx={{
        color: "text.secondary",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.4,
        alignSelf: "center",
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        color: "text.primary",
        fontWeight: 500,
        fontSize: copyable ? 12 : 14,
        fontFamily: copyable ? "ui-monospace, SFMono-Regular, Menlo, monospace" : undefined,
        wordBreak: "break-all",
      }}
    >
      {value || <Box component="span" sx={{ color: "text.disabled" }}>—</Box>}
    </Typography>
  </Box>
);

const SectionTitle = ({ children }) => (
  <Typography
    variant="overline"
    sx={{
      display: "block",
      fontWeight: 700,
      color: "text.secondary",
      letterSpacing: 0.8,
      mb: 1,
    }}
  >
    {children}
  </Typography>
);

const FinancialTile = ({ label, value, accent }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: 1.5,
      border: (t) => `1px solid ${t.palette.divider}`,
      bgcolor: "background.default",
    }}
  >
    <Typography
      variant="caption"
      sx={{
        display: "block",
        textTransform: "uppercase",
        letterSpacing: 0.5,
        fontWeight: 700,
        color: "text.secondary",
        mb: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="h6"
      sx={{
        fontWeight: 800,
        color: accent || "text.primary",
        letterSpacing: -0.3,
      }}
    >
      {formatMoney(value)}
    </Typography>
  </Box>
);

export const ProfileData = (props) => {
  const g = props.graphData || {};
  const c = props.customerData || {};

  const fullName = [g.givenName, g.surname].filter(Boolean).join(" ").trim();
  const email = g.userPrincipalName || g.mail || "";

  const balanceDue = Number(c.balanceDueLCY) || 0;
  const remaining =
    c.remainingCreditLimitLCY != null
      ? c.remainingCreditLimitLCY
      : (Number(c.creditLimitLCY) || 0) - (Number(c.balanceLCY) || 0) - (Number(c.outstandingOrdersLCY) || 0);

  return (
    <Box>
      {/* Header strip */}
      <Card
        sx={{
          mb: 3,
          p: { xs: 2, md: 3 },
          border: (t) => `1px solid ${t.palette.divider}`,
          boxShadow: "none",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase" }}>
              Profile
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.25, lineHeight: 1.1 }}>
              {fullName || "Account"}
            </Typography>
            {email && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {email}
              </Typography>
            )}
          </Box>
        </Stack>
      </Card>

      {/* Customer details */}
      <Card
        sx={{
          p: { xs: 2, md: 3 },
          mb: 3,
          border: (t) => `1px solid ${t.palette.divider}`,
          boxShadow: "none",
        }}
      >
        <SectionTitle>Customer</SectionTitle>
        <Box>
          <DataRow label="Customer no" value={c.no} copyable />
          <DataRow label="Customer name" value={c.name} />
          <DataRow label="Email" value={c.email} />
          <DataRow label="Phone" value={c.mobilePhoneNo} />
          <DataRow label="Address" value={c.address} />
          <DataRow label="Payment terms" value={c.paymentTerms} />
        </Box>
      </Card>

      {/* Financials */}
      <Card
        sx={{
          p: { xs: 2, md: 3 },
          border: (t) => `1px solid ${t.palette.divider}`,
          boxShadow: "none",
        }}
      >
        <SectionTitle>Account financials</SectionTitle>
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            mt: 1,
          }}
        >
          <FinancialTile label="Credit limit" value={c.creditLimitLCY} />
          <FinancialTile
            label="Remaining credit"
            value={remaining}
            accent={remaining < 0 ? "error.main" : "primary.main"}
          />
          <FinancialTile label="Outstanding orders" value={c.outstandingOrdersLCY} />
          <FinancialTile label="Balance" value={c.balanceLCY} />
          <FinancialTile
            label="Balance due"
            value={balanceDue}
            accent={balanceDue > 0 ? "error.main" : "text.primary"}
          />
        </Box>
      </Card>
    </Box>
  );
};
